import React, { useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Button,
  Card,
  CardContent,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Alert,
  Grid,
} from '@mui/material';
import QrCode2 from '@mui/icons-material/QrCode2';
import Share from '@mui/icons-material/Share';
import Close from '@mui/icons-material/Close';
import ContentCopy from '@mui/icons-material/ContentCopy';
import Check from '@mui/icons-material/Check';
import { QRCodeSVG as QRCode } from 'qrcode.react';
import { useCart } from '../../contexts/CartContext';

export const PublicCartAccess: React.FC = () => {
  const { cart } = useCart();
  const [qrDialogOpen, setQrDialogOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  const publicCartUrl = `${window.location.origin}/public/cart/${cart.id}`;

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(publicCartUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy link', err);
    }
  };

  const handleDownloadQR = () => {
    const canvas = document.getElementById('qr-code-canvas') as HTMLCanvasElement;
    if (canvas) {
      const url = canvas.toDataURL('image/png');
      const link = document.createElement('a');
      link.download = `cart-qr-${cart.id}.png`;
      link.href = url;
      link.click();
    }
  };

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom fontWeight={600}>
          Share Your Cart
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Generate a QR code or shareable link for community members
        </Typography>
      </Box>

      <Alert severity="info" sx={{ mb: 3 }}>
        Community members can scan the QR code or use the link to access and purchase items from this cart.
        Perfect for event tickets and public sales!
      </Alert>

      <Card>
        <CardContent>
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Paper
                variant="outlined"
                sx={{
                  p: 3,
                  textAlign: 'center',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  '&:hover': {
                    bgcolor: 'primary.50',
                    borderColor: 'primary.main',
                  },
                }}
                onClick={() => setQrDialogOpen(true)}
              >
                <QrCode2 sx={{ fontSize: 80, color: 'primary.main', mb: 2 }} />
                <Typography variant="h6" gutterBottom fontWeight={600}>
                  Generate QR Code
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Create a QR code for easy mobile access
                </Typography>
                <Button
                  variant="contained"
                  sx={{ mt: 2 }}
                  onClick={e => {
                    e.stopPropagation();
                    setQrDialogOpen(true);
                  }}
                >
                  View QR Code
                </Button>
              </Paper>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <Paper
                variant="outlined"
                sx={{
                  p: 3,
                  textAlign: 'center',
                }}
              >
                <Share sx={{ fontSize: 80, color: 'secondary.main', mb: 2 }} />
                <Typography variant="h6" gutterBottom fontWeight={600}>
                  Share Link
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Copy and share the direct link
                </Typography>
                <Button
                  variant="contained"
                  color="secondary"
                  startIcon={copied ? <Check /> : <ContentCopy />}
                  sx={{ mt: 2 }}
                  onClick={handleCopyLink}
                >
                  {copied ? 'Copied!' : 'Copy Link'}
                </Button>
              </Paper>
            </Grid>
          </Grid>

          <Paper variant="outlined" sx={{ p: 2, mt: 3, bgcolor: 'grey.50' }}>
            <Typography variant="caption" fontWeight={600} display="block" gutterBottom>
              Shareable Link:
            </Typography>
            <Typography
              variant="body2"
              sx={{
                wordBreak: 'break-all',
                fontFamily: 'monospace',
                bgcolor: 'white',
                p: 1,
                borderRadius: 1,
              }}
            >
              {publicCartUrl}
            </Typography>
          </Paper>

          <Alert severity="warning" sx={{ mt: 3 }}>
            <Typography variant="caption">
              This link provides direct access to purchase items in this cart. Share only with intended recipients.
            </Typography>
          </Alert>
        </CardContent>
      </Card>

      <Dialog
        open={qrDialogOpen}
        onClose={() => setQrDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6" fontWeight={600}>
              Cart QR Code
            </Typography>
            <IconButton onClick={() => setQrDialogOpen(false)}>
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ textAlign: 'center', py: 3 }}>
            <Paper
              elevation={0}
              sx={{ display: 'inline-block', p: 3, bgcolor: 'white', border: 2, borderColor: 'divider' }}
            >
              <QRCode
                id="qr-code-canvas"
                value={publicCartUrl}
                size={256}
                level="H"
              />
            </Paper>

            <Typography variant="body2" color="text.secondary" sx={{ mt: 3, mb: 2 }}>
              Scan this QR code with a mobile device to access the cart
            </Typography>

            <Button
              variant="contained"
              fullWidth
              onClick={handleDownloadQR}
              sx={{ mb: 1 }}
            >
              Download QR Code
            </Button>

            <Button
              variant="outlined"
              fullWidth
              startIcon={copied ? <Check /> : <ContentCopy />}
              onClick={handleCopyLink}
            >
              {copied ? 'Link Copied!' : 'Copy Link'}
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    </Container>
  );
};
